<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Layer, Map as LeafletMap } from 'leaflet';

export interface TrainingCentreDirectoryMapPin {
  lat: number;
  lng: number;
  title: string;
  subtitle?: string;
  href: string;
}

const props = withDefaults(
  defineProps<{
    pins: TrainingCentreDirectoryMapPin[];
    /** Map canvas height in CSS pixels. */
    heightPx?: number;
    /** When set, used as the map canvas `aria-label` instead of the training-centre list default. */
    canvasAriaLabel?: string | null;
  }>(),
  {
    heightPx: 320,
    canvasAriaLabel: null,
  }
);

const { t } = useI18n();

const rootEl = ref<HTMLElement | null>(null);
const mapInstance = shallowRef<LeafletMap | null>(null);
let mapSetupToken = 0;

const canvasStyle = computed(() => ({
  height: `${props.heightPx}px`,
}));

const ariaLabel = computed(() =>
  (props.canvasAriaLabel || '').trim() || t('trainingCentreList.directoryMapCanvasAria')
);

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function popupHtml(pin: TrainingCentreDirectoryMapPin): string {
  const title = escapeHtml(pin.title);
  const sub = (pin.subtitle || '').trim();
  const subHtml = sub ? `<div class="tc-dir-map-popup__sub">${escapeHtml(sub)}</div>` : '';
  const linkLabel = escapeHtml(t('trainingCentreList.viewDetails'));
  const href = escapeHtml(pin.href);
  return `<div class="tc-dir-map-popup"><div class="tc-dir-map-popup__title">${title}</div>${subHtml}<a class="tc-dir-map-popup__link" href="${href}">${linkLabel}</a></div>`;
}

function removeMap() {
  mapInstance.value?.remove();
  mapInstance.value = null;
}

async function setupMap() {
  const token = ++mapSetupToken;
  removeMap();
  if (props.pins.length === 0 || !rootEl.value) return;

  const leaflet = await import('leaflet');
  if (token !== mapSetupToken || !rootEl.value || props.pins.length === 0) return;

  await import('leaflet/dist/leaflet.css');
  if (token !== mapSetupToken || !rootEl.value || props.pins.length === 0) return;

  const L = leaflet.default;

  const map = L.map(rootEl.value, {
    scrollWheelZoom: false,
    attributionControl: true,
  });

  L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxZoom: 19,
  }).addTo(map);

  const layers: Layer[] = [];
  for (const pin of props.pins) {
    const m = L.circleMarker([pin.lat, pin.lng], {
      radius: 6,
      color: '#1d4ed8',
      weight: 2,
      fillColor: '#3b82f6',
      fillOpacity: 0.88,
    }).addTo(map);
    m.bindPopup(popupHtml(pin));
    layers.push(m);
  }

  if (layers.length === 1) {
    const first = props.pins[0];
    if (first) map.setView([first.lat, first.lng], 12);
  } else {
    const fg = L.featureGroup(layers);
    map.fitBounds(fg.getBounds(), { padding: [28, 28], maxZoom: 11 });
  }

  if (token !== mapSetupToken) {
    map.remove();
    return;
  }

  mapInstance.value = map;
  await nextTick();
  requestAnimationFrame(() => {
    if (token === mapSetupToken) map.invalidateSize();
  });
}

function relayout() {
  mapInstance.value?.invalidateSize();
}

defineExpose({ relayout });

onMounted(() => {
  void setupMap();
});

watch(
  () => props.pins,
  () => {
    void setupMap();
  },
  { deep: true, flush: 'post' }
);

watch(
  () => props.heightPx,
  () => {
    void nextTick(() => relayout());
  }
);

onBeforeUnmount(() => {
  mapSetupToken += 1;
  removeMap();
});
</script>

<template>
  <div v-if="pins.length > 0" class="tc-dir-map">
    <div
      ref="rootEl"
      class="tc-dir-map__canvas"
      :style="canvasStyle"
      role="img"
      :aria-label="ariaLabel"
    />
  </div>
</template>

<style scoped>
.tc-dir-map {
  width: 100%;
}

.tc-dir-map__canvas {
  width: 100%;
  border-radius: 0 0 6px 6px;
  z-index: 0;
}

.tc-dir-map__canvas :deep(.leaflet-control-attribution) {
  font-size: 0.65rem;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>

<!-- Popup HTML is injected by Leaflet outside Vue's scoped subtree. -->
<style>
.leaflet-popup-content .tc-dir-map-popup {
  font-size: 0.8125rem;
  line-height: 1.35;
  min-width: 8rem;
}

.leaflet-popup-content .tc-dir-map-popup__title {
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: #111827;
}

.leaflet-popup-content .tc-dir-map-popup__sub {
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
}

.leaflet-popup-content .tc-dir-map-popup__link {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1d4ed8;
}
</style>
